/**
 * Draft-room actions that call an edge function.
 *
 * Extracted from the page so the REQUEST each one makes is testable without rendering a
 * Radix dropdown into a portal. jsdom does not implement PointerEvent, so a menu item
 * behind a Radix trigger cannot be clicked in a unit test at all — the logic would
 * otherwise be unreachable, and "the handler is wired" would be the only assertable thing.
 *
 * Every function here is a VERBATIM port of the corresponding handler in NFLDraftPage:
 * same endpoint, same headers, same body, same success/error branches. The live room is
 * unchanged; this is a second caller of the same endpoints, not a new protocol.
 */

export interface ActionDeps {
  /** Base URL, i.e. supabase.supabaseUrl. */
  supabaseUrl: string;
  /** Resolves the caller's access token, or null when there is no session. */
  getAccessToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}

export interface ActionResult {
  ok: boolean;
  /** The parsed body, when there was one. */
  data?: Record<string, unknown>;
  /** Server-supplied error string, or a transport message. */
  error?: string;
}

async function callEdge(
  fn: string,
  body: Record<string, unknown>,
  deps: ActionDeps,
): Promise<ActionResult> {
  const doFetch = deps.fetchImpl ?? fetch;
  const token = await deps.getAccessToken();
  if (!token) return { ok: false, error: 'Not logged in' };

  try {
    const res = await doFetch(`${deps.supabaseUrl}/functions/v1/${fn}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (data?.error) return { ok: false, error: String(data.error), data };
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: (err as Error)?.message ?? String(err) };
  }
}

/**
 * Pause or resume the draft.
 *
 * ALWAYS via nfl-pause-draft. That function carries SAB-841's MIN_RESUME_SECONDS floor
 * (30s, clamped to the league's pick timer), which is what stops a pause taken near 0:00
 * from stranding the draft: nfl_pause_resume_lock stores GREATEST(0, FLOOR(remaining)), so
 * a resume computed from a stored 0 yields `now + 0`, the RPC refuses it with BAD_DEADLINE,
 * and auto-pick skips any league carrying draft_paused_at — nothing would ever advance it
 * again. Do not reimplement this against the RPC directly.
 */
export function pauseResumeDraft(
  leagueId: string,
  action: 'pause' | 'resume',
  deps: ActionDeps,
): Promise<ActionResult> {
  return callEdge('nfl-pause-draft', { league_id: leagueId, action }, deps);
}

/**
 * Make a pick.
 *
 * `commissioner_pick` is computed by the CALLER and sent in the body, exactly as the live
 * room does. It must be `isCommissioner && !isMyTurn`: nfl_acquire_pick_lock re-validates
 * it, and a commissioner picking on someone else's behalf without the flag is refused with
 * NOT_YOUR_TURN. Sending it unconditionally would be worse — it would let a commissioner
 * silently override their own turn checks.
 *
 * NOT OPTIMISTIC. The caller refetches after this resolves, which is the shipped
 * behaviour. Optimistic removal is a separate change and has to decide UI behaviour for
 * each refusal (PLAYER_ALREADY_DRAFTED, STALE_POINTER, NOT_YOUR_TURN).
 */
export function makePick(
  leagueId: string,
  playerId: string,
  commissionerPick: boolean,
  deps: ActionDeps,
): Promise<ActionResult> {
  return callEdge(
    'nfl-make-pick',
    { league_id: leagueId, player_id: playerId, commissioner_pick: commissionerPick },
    deps,
  );
}

/** Undo the last pick, or cancel the draft. Both are commissioner-only and irreversible. */
export function callDraftAdmin(
  fn: 'nfl-undo-pick' | 'nfl-cancel-draft',
  leagueId: string,
  deps: ActionDeps,
): Promise<ActionResult> {
  return callEdge(fn, { league_id: leagueId }, deps);
}

/** Auto-draft every remaining pick. Server-chunked; the caller re-invokes until complete. */
export function autoDraftAll(leagueId: string, deps: ActionDeps): Promise<ActionResult> {
  return callEdge('nfl-auto-draft-all', { league_id: leagueId }, deps);
}

/**
 * SAB-927: initialize the draft.
 *
 * The body is `{ league_id }` and nothing else -- verbatim from the legacy caller at
 * NFLDraftPage.tsx:388, which was the only client caller of this endpoint in the app and
 * had been unreachable since the routing cutover. `rounds` and `seconds_per_pick` are NOT
 * sent: the function derives rounds from the roster template and the timer from the
 * league's own draft_pick_timer, and passing them here would let the room override a
 * league setting the commissioner configured elsewhere.
 *
 * On refusal the parsed body is returned alongside the error so the caller can read
 * `code` -- COMMISSIONER_UNPAID is the one the room translates into friendlier copy.
 */
export function initializeDraft(leagueId: string, deps: ActionDeps): Promise<ActionResult> {
  return callEdge('nfl-initialize-draft', { league_id: leagueId }, deps);
}
